class GeocodingService {
  constructor() {
    this.cache = new Map();
    this.CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 horas
    this.DISTANCE_THRESHOLD = 100; // metros para proximidad geográfica
    this.requestQueue = [];
    this.processing = false;
    this.rateLimitDelay = 1000;
    this.lastRequestTime = 0;
    
    // Configuración de proveedores con prioridades y límites
    this.providers = {
      nominatim: {
        url: 'https://nominatim.openstreetmap.org/reverse',
        priority: 1,
        rateLimit: 1000,
        maxRetries: 2
      },
      google: {
        url: 'https://maps.googleapis.com/maps/api/geocode/json',
        priority: 2,
        rateLimit: 100,
        maxRetries: 1,
        apiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY
      }
    };
    
    this.stats = {
      totalRequests: 0,
      cacheHits: 0,
      nominatimSuccess: 0,
      googleFallbacks: 0,
      errors: 0
    };

    this._loadCacheFromStorage();
  }

  /**
   * Método principal de geocodificación con cache inteligente y múltiples proveedores
   */
  async geocodeCoordinates(latitude, longitude, vehicleId = null) {
    const startTime = Date.now();
    this.stats.totalRequests++;

    try {
      // Validación y normalización de coordenadas
      const lat = parseFloat(latitude);
      const lon = parseFloat(longitude);
      
      if (isNaN(lat) || isNaN(lon)) {
        throw new Error('Coordenadas inválidas');
      }

      // Búsqueda en cache con algoritmo de proximidad geográfica
      const cachedResult = this._findInCache(lat, lon);
      if (cachedResult) {
        this.stats.cacheHits++;
        return {
          success: true,
          address: cachedResult.address,
          source: 'cache',
          coordinates: { lat, lon },
          processingTime: Date.now() - startTime
        };
      }

      // Procesamiento con proveedores externos
      const result = await this._processWithProviders(lat, lon);
      
      // Persistencia en cache para futuras consultas
      if (result.success) {
        this._saveToCache(lat, lon, result.address, result.source);
        this._saveCacheToStorage();
      }

      return {
        ...result,
        coordinates: { lat, lon },
        processingTime: Date.now() - startTime
      };

    } catch (error) {
      this.stats.errors++;
      
      return {
        success: false,
        error: error.message,
        address: 'Error obteniendo ubicación',
        source: 'error',
        coordinates: { latitude, longitude },
        processingTime: Date.now() - startTime
      };
    }
  }

  /**
   * Geocodificación mediante OpenStreetMap Nominatim
   */
  async _geocodeWithNominatim(lat, lon) {
    const url = `${this.providers.nominatim.url}?format=json&lat=${lat}&lon=${lon}&zoom=18&addressdetails=1&accept-language=es`;
    
    await this._waitForRateLimit('nominatim');
    
    const response = await fetch(url, {
      headers: {
        'User-Agent': 'VehicleTracker/1.0 (contact@example.com)',
        'Accept': 'application/json'
      },
      signal: AbortSignal.timeout(10000)
    });

    if (!response.ok) {
      throw new Error(`Nominatim HTTP ${response.status}`);
    }

    const data = await response.json();
    this.lastRequestTime = Date.now();

    if (data && data.display_name) {
      this.stats.nominatimSuccess++;
      return this._formatNominatimAddress(data);
    }

    throw new Error('Sin resultados en Nominatim');
  }

  /**
   * Geocodificación mediante Google Maps API como fallback
   */
  async _geocodeWithGoogle(lat, lon) {
    if (!this.providers.google.apiKey) {
      throw new Error('Google Maps API key no configurada');
    }

    const url = `${this.providers.google.url}?latlng=${lat},${lon}&key=${this.providers.google.apiKey}&language=es&result_type=street_address|route|locality`;
    
    await this._waitForRateLimit('google');
    
    const response = await fetch(url, {
      signal: AbortSignal.timeout(8000)
    });

    if (!response.ok) {
      throw new Error(`Google Maps HTTP ${response.status}`);
    }

    const data = await response.json();
    this.lastRequestTime = Date.now();

    if (data.status === 'OK' && data.results && data.results.length > 0) {
      this.stats.googleFallbacks++;
      return this._formatGoogleAddress(data.results[0]);
    }

    throw new Error(`Google geocoding: ${data.status}`);
  }

  /**
   * Procesador con arquitectura de múltiples proveedores y fallbacks automáticos
   */
  async _processWithProviders(lat, lon) {
    let lastError = null;

    // Prioridad 1: Nominatim (OpenStreetMap)
    try {
      const address = await this._geocodeWithNominatim(lat, lon);
      return {
        success: true,
        address,
        source: 'nominatim'
      };
    } catch (error) {
      lastError = error;
    }

    // Fallback automático a Google Maps
    try {
      const address = await this._geocodeWithGoogle(lat, lon);
      return {
        success: true,
        address,
        source: 'google'
      };
    } catch (error) {
      throw new Error(`Todos los proveedores fallaron. Último error: ${error.message}`);
    }
  }

  /**
   * Búsqueda inteligente en cache con algoritmo de proximidad geográfica
   */
  _findInCache(lat, lon) {
    const now = Date.now();
    
    for (const [key, cached] of this.cache) {
      // Limpieza automática de entradas expiradas
      if (now - cached.timestamp > this.CACHE_DURATION) {
        this.cache.delete(key);
        continue;
      }

      // Verificación de proximidad geográfica mediante algoritmo de Haversine
      const distance = this._calculateDistance(lat, lon, cached.lat, cached.lon);
      if (distance <= this.DISTANCE_THRESHOLD) {
        cached.hits = (cached.hits || 0) + 1;
        cached.lastUsed = now;
        return cached;
      }
    }
    
    return null;
  }

  /**
   * Almacenamiento en cache con gestión de tamaño y metadatos
   */
  _saveToCache(lat, lon, address, source) {
    const key = this._generateCacheKey(lat, lon);
    this.cache.set(key, {
      address,
      lat,
      lon,
      source,
      timestamp: Date.now(),
      hits: 1,
      lastUsed: Date.now()
    });

    // Control de tamaño del cache con limpieza automática
    if (this.cache.size > 1000) {
      this._cleanOldestCacheEntries();
    }
  }

  /**
   * Cálculo de distancia geográfica usando fórmula de Haversine
   */
  _calculateDistance(lat1, lon1, lat2, lon2) {
    const R = 6371e3; // Radio terrestre en metros
    const φ1 = lat1 * Math.PI / 180;
    const φ2 = lat2 * Math.PI / 180;
    const Δφ = (lat2 - lat1) * Math.PI / 180;
    const Δλ = (lon2 - lon1) * Math.PI / 180;

    const a = Math.sin(Δφ/2) * Math.sin(Δφ/2) +
              Math.cos(φ1) * Math.cos(φ2) *
              Math.sin(Δλ/2) * Math.sin(Δλ/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));

    return R * c;
  }

  /**
   * Generador de claves de cache con redondeo para zonas geográficas
   * @private
   * @param {number} lat - Latitud
   * @param {number} lon - Longitud
   * @returns {string} Clave única de cache
   */
  _generateCacheKey(lat, lon) {
    const roundedLat = Math.round(lat * 10000) / 10000;
    const roundedLon = Math.round(lon * 10000) / 10000;
    return `${roundedLat},${roundedLon}`;
  }

  /**
   * Gestión de rate limiting por proveedor
   */
  async _waitForRateLimit(provider) {
    const now = Date.now();
    const timeSinceLastRequest = now - this.lastRequestTime;
    const rateLimit = this.providers[provider].rateLimit;
    
    if (timeSinceLastRequest < rateLimit) {
      const waitTime = rateLimit - timeSinceLastRequest;
      await new Promise(resolve => setTimeout(resolve, waitTime));
    }
  }

  /**
   * Formateador de direcciones para respuestas de Nominatim
   */
  _formatNominatimAddress(data) {
    const address = data.address || {};
    const parts = [];

    if (address.house_number) parts.push(address.house_number);
    if (address.road) parts.push(address.road);
    if (address.neighbourhood) parts.push(address.neighbourhood);
    if (address.suburb) parts.push(address.suburb);
    if (address.city || address.town || address.village) {
      parts.push(address.city || address.town || address.village);
    }
    if (address.state) parts.push(address.state);
    if (address.country) parts.push(address.country);

    return parts.length > 0 ? parts.join(', ') : data.display_name;
  }

  /**
   * Formateador de direcciones para respuestas de Google Maps
   */
  _formatGoogleAddress(result) {
    return result.formatted_address;
  }

  /**
   * Carga de cache desde localStorage al inicializar
   */
  _loadCacheFromStorage() {
    try {
      const stored = localStorage.getItem('geocoding_cache');
      if (stored) {
        const data = JSON.parse(stored);
        const now = Date.now();
        
        data.forEach(([key, cached]) => {
          if (now - cached.timestamp < this.CACHE_DURATION) {
            this.cache.set(key, cached);
          }
        });
      }
    } catch (error) {
      // Silenciar errores de localStorage para no interrumpir la funcionalidad
    }
  }

  /**
   * Persistencia de cache en localStorage
   */
  _saveCacheToStorage() {
    try {
      const data = Array.from(this.cache.entries());
      localStorage.setItem('geocoding_cache', JSON.stringify(data));
    } catch (error) {
      // Silenciar errores de localStorage
    }
  }

  /**
   * Limpieza de entradas de cache más antiguas basada en uso
   */
  _cleanOldestCacheEntries() {
    const entries = Array.from(this.cache.entries());
    entries.sort((a, b) => a[1].lastUsed - b[1].lastUsed);
    
    const toDelete = entries.slice(0, 100);
    toDelete.forEach(([key]) => this.cache.delete(key));
  }

  /**
   * Obtiene estadísticas completas del servicio
   */
  getStats() {
    return {
      ...this.stats,
      cacheSize: this.cache.size,
      cacheHitRate: this.stats.totalRequests > 0 ? 
        Math.round((this.stats.cacheHits / this.stats.totalRequests) * 100) : 0
    };
  }

  /**
   * Limpieza completa de cache local y persistente
   */
  clearCache() {
    this.cache.clear();
    localStorage.removeItem('geocoding_cache');
  }
}

export default new GeocodingService();